import React, {FC, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import {FilterIcon, GuidelineIcon} from '@epic-ui/icons';
import {useTheme} from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import {numberFormatter} from '@epic-visualizations/charts';
import {ReferenceLine} from 'recharts';

import {Tooltip} from '../../../../../components';
import {Widget} from '../../../../common/widget';
import PerformanceChart from '../components/chart/PerformanceChart';
import HorizontalLabel from '../components/HorizontalLabel';
import IntervalRadioGroup from '../components/interval-radio-group/IntervalRadioGroup';
import RadioGameSelect from '../../../../radio-game-select/RadioGameSelect';
import WidgetDatePicker from '../../../../widget-date-picker/WidgetDatePicker';
import WidgetDownloadButton from '../components/WidgetDownloadButton';
import TimeScaleSelect from '../../../../time-scale-select/TimeScaleSelect';
import RadioOptionSelect from '../../../../radio-option-select/RadioOptionSelect';
import IconButton from '../../../../common/IconButton';
import useChartTrends from '../hooks/useChartTrends';
import useReferenceLines from '../hooks/useReferenceLines';
import useReleases from '../hooks/useReleases';
import useLocalization from '../../../../../hooks/useLocalization';
import {PDFPictureTrap} from '../export/PDFExportProvider';
import usePerformanceData from './usePerformanceData';
import {prepareLineChartExportData} from '../exportUtils';
import {
    getWidgetTimeRangeDates,
    dateFormatter,
    axisPercentFormatter,
    percentFormatter
} from '../utils';
import {useRangePickerData} from '../../../../../components/common/date-range-picker';
import InsufficientWauMessage from './InsufficientWauMessage';
import {WEEK_DATE_FORMAT, TIME_FORMAT, MIN_WAU} from '../constants';
import useTimeRangeFormatter from '../hooks/useTimeRangeFormatter';

import {WidgetProps} from '../types';
import {Status, DateRange, TimeScale, CombinedPlatform} from '../../../../../types';
import {Metric} from '../../../../../../shared/types/FortniteGames';

import {OptionWrapper, ControlsWrapper, SelectWrapper, IconWrapper, Spacer} from '../styles';

const TREND_MULTIPLIER = -1;
const CODES = {
    showMe: 'epic.affiliate.fortnite_games.show_me',
    timeScale: 'epic.affiliate.fortnite_games.time_scale.',
    title: 'epic.affiliate.fortnite_games.performance.title',
    tooltip: 'epic.affiliate.fortnite_games.performance.tooltip',
    metric: 'epic.affiliate.fortnite_games.performance.metric.',
    metricTooltip: 'epic.affiliate.fortnite_games.performance.metric.tooltip',
    platform: 'epic.affiliate.fortnite_games.performance.platform.'
};

const dateFormat = {
    [TimeScale.HOURLY]: {...WEEK_DATE_FORMAT, ...TIME_FORMAT},
    [TimeScale.DAILY]: WEEK_DATE_FORMAT
};

const tooltipFormatters = {
    [Metric.MISSED_FRAMES]: percentFormatter,
    [Metric.HITCHES_RATE]: numberFormatter
};

const yAxisFormatters = {
    [Metric.MISSED_FRAMES]: axisPercentFormatter
};

const PerformanceWidget: FC<WidgetProps> = ({
    selectedGames,
    gamesList,
    keysMap,
    gamesLoading
}: WidgetProps) => {
    const getMessage = useLocalization();

    const theme = useTheme();
    const isFmdBreakpoint = useMediaQuery(theme.breakpoints.up('fmd'));
    const isFlgBreakpoint = useMediaQuery(theme.breakpoints.up('flg'));

    const enableFnPerformanceZeroLimit = useSelector((state: any) =>
        state.get('config').get('enableFnPerformanceZeroLimit')
    );

    const selectedGame = selectedGames[0];

    const timeScaleOptions = useMemo(() => {
        return Object.values(TimeScale)
            .slice(1, 3)
            .map(value => ({
                label: getMessage(`${CODES.timeScale}${value}`),
                value
            }));
    }, []);

    const platformOptions = useMemo(() => {
        return Object.values(CombinedPlatform).map(value => ({
            label: getMessage(`${CODES.platform}${value}`),
            value
        }));
    }, []);

    const metricOptions = useMemo(() => {
        return Object.values(Metric).map(value => ({
            label: getMessage(`${CODES.metric}${value}`),
            value
        }));
    }, []);

    const [timeScale, setTimeScale] = useState(timeScaleOptions[1]);
    const [platform, setPlatform] = useState(platformOptions[0]);
    const [metric, setMetric] = useState(metricOptions[0]);
    const [game, setGame] = useState(selectedGame);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    const {selectedDate, startDate, endDate, setCalendar} = useRangePickerData(
        getWidgetTimeRangeDates(TimeScale.DAILY)
    );

    const axisFormatter = useTimeRangeFormatter({startDate, endDate, timeScale: timeScale.value});

    const creativeIslandCode =
        (game && (game.value as string)) || (selectedGame && (selectedGame.value as string));

    const {data, versions, wau, loading, error, reload} = usePerformanceData({
        creativeIslandCode,
        platform: platform.value,
        metric: metric.value,
        timeScale: timeScale.value,
        startDate,
        endDate
    });

    const referenceLines = useReferenceLines({versions, timeScale: timeScale.value});

    const isLoading = loading || gamesLoading;
    const isInsufficientWauMessage = !isLoading && !enableFnPerformanceZeroLimit && wau < MIN_WAU;

    const trends = useChartTrends({data, xAxis: 'time', multiplier: TREND_MULTIPLIER});
    const releases = useReleases(versions, timeScale.value);

    // Handlers
    const handleRangeChange = (dateRange: DateRange) => {
        setCalendar(dateRange);
    };

    const handleTimeScaleChange = timeScale => {
        const timeRangeDates = getWidgetTimeRangeDates(timeScale.value);

        setTimeScale(timeScale);
        setCalendar(timeRangeDates);
    };

    const handleGameChange = (_event, gameOption) => {
        setGame(gameOption);
    };

    const handleMobileMenuClick = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    // Effects
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [isFmdBreakpoint]);

    const GameSelect = (
        <RadioGameSelect
            options={gamesList}
            value={game || selectedGame}
            onChange={handleGameChange}
        />
    );

    const TimeSelect = (
        <TimeScaleSelect
            options={timeScaleOptions}
            value={timeScale}
            onChange={handleTimeScaleChange}
        />
    );

    const DatePicker = (
        <WidgetDatePicker
            timeScale={timeScale.value}
            selectedDate={selectedDate}
            startDate={startDate}
            endDate={endDate}
            onRangeChange={handleRangeChange}
        />
    );

    return (
        <PDFPictureTrap id={CODES.title} index={1}>
            <Widget
                height={585}
                header={getMessage(CODES.title)}
                padding="15px"
                tooltip={getMessage(CODES.tooltip)}
                status={error ? Status.ERROR : undefined}
                message={getMessage(error) || error}
                overflow="visible"
                retry={reload}
                headerContent={
                    isFmdBreakpoint && (
                        <Box mt={1} width="460px">
                            {GameSelect}
                        </Box>
                    )
                }
                actions={
                    <>
                        {isFmdBreakpoint ? (
                            <>
                                {isFlgBreakpoint ? (
                                    <HorizontalLabel>
                                        {getMessage(CODES.showMe)}
                                        <IntervalRadioGroup
                                            options={timeScaleOptions}
                                            value={timeScale}
                                            onChange={handleTimeScaleChange}
                                        />
                                    </HorizontalLabel>
                                ) : (
                                    <Box mx={1.5} width={140}>
                                        {TimeSelect}
                                    </Box>
                                )}
                                <Box mx={1.5} width={233}>
                                    {DatePicker}
                                </Box>
                            </>
                        ) : (
                            <IconButton onClick={handleMobileMenuClick} isOpen={isMobileMenuOpen}>
                                <FilterIcon />
                            </IconButton>
                        )}
                        <WidgetDownloadButton
                            areaId={CODES.title}
                            fileName={getMessage(CODES.title)}
                            data={data}
                            prepareCSVData={prepareLineChartExportData(keysMap)}
                            disabled={isLoading || !!error}
                        />
                    </>
                }
                options={
                    isMobileMenuOpen &&
                    !isFmdBreakpoint && (
                        <OptionWrapper>
                            {GameSelect}
                            {TimeSelect}
                            {DatePicker}
                        </OptionWrapper>
                    )
                }>
                <Box width="100%">
                    {isInsufficientWauMessage ? (
                        <InsufficientWauMessage wau={wau} />
                    ) : (
                        <>
                            {!isLoading && (
                                <Box display="flex" alignItems="center">
                                    <ControlsWrapper>
                                        <SelectWrapper m={1}>
                                            <RadioOptionSelect
                                                options={platformOptions}
                                                value={platform}
                                                onChange={setPlatform}
                                            />
                                        </SelectWrapper>
                                        <Spacer />
                                        <SelectWrapper m={1}>
                                            <RadioOptionSelect
                                                options={metricOptions}
                                                value={metric}
                                                onChange={setMetric}
                                            />
                                        </SelectWrapper>
                                    </ControlsWrapper>
                                    <Tooltip
                                        placement="top"
                                        title={getMessage(CODES.metricTooltip)}>
                                        <IconWrapper>
                                            <GuidelineIcon />
                                        </IconWrapper>
                                    </Tooltip>
                                </Box>
                            )}

                            <PerformanceChart
                                height={490}
                                data={data}
                                trends={trends}
                                releases={releases}
                                xTickFormatter={axisFormatter}
                                tooltipLabelFormatter={dateFormatter(dateFormat[timeScale.value])}
                                tooltipFormatter={tooltipFormatters[metric.value]}
                                yTickFormatter={yAxisFormatters[metric.value]}
                                loading={isLoading}
                                yAxisProps={{
                                    reversed: true,
                                    interval: 'preserveStartEnd'
                                }}>
                                {referenceLines.length &&
                                    referenceLines.map(referenceLine => (
                                        <ReferenceLine
                                            key={referenceLine.x || referenceLine.y}
                                            {...referenceLine}
                                        />
                                    ))}
                            </PerformanceChart>
                        </>
                    )}
                </Box>
            </Widget>
        </PDFPictureTrap>
    );
};

export default PerformanceWidget;
